import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import {
  decodeTransferFrame,
  isValidSessionId,
  QR_PROTOCOL_VERSION,
  type TransferFrame,
} from "../../protocol";

const DATABASE_VERSION = 2;
const SESSION_SCHEMA_VERSION = 1 as const;
const RECEIVER_FRAME_STORE = "qr-frames";
const SENDER_FRAME_STORE = "qr-sender-frames";
const SESSION_STORE = "qr-sessions";
const DEFAULT_RECEIVER_DISPLAY_NAME = "Received QR transfer";
const MAX_DISPLAY_NAME_LENGTH = 255;
const MIN_FRAME_DURATION_MS = 150;
const MAX_FRAME_DURATION_MS = 3_000;
const textEncoder = new TextEncoder();

export type QrSessionRole = "sender" | "receiver";
export type SenderQrSessionStatus = "prepared" | "streaming" | "paused";
export type ReceiverQrSessionStatus = "receiving" | "paused" | "verified" | "conflict";

interface QrSessionRecordBase {
  schemaVersion: typeof SESSION_SCHEMA_VERSION;
  qrProtocolVersion: typeof QR_PROTOCOL_VERSION;
  sessionId: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
  storedBytes: number;
}

export interface SenderQrSessionRecord extends QrSessionRecordBase {
  role: "sender";
  status: SenderQrSessionStatus;
  fileCount: number;
  preparedBytes: number;
  frameCount: number;
  frameDurationMs: number;
}

export interface ReceiverQrSessionRecord extends QrSessionRecordBase {
  role: "receiver";
  status: ReceiverQrSessionStatus;
  fileCount: number | null;
  preparedBytes: number | null;
  frameCount: number | null;
  receivedFrames: number;
}

export type QrSessionRecord = SenderQrSessionRecord | ReceiverQrSessionRecord;

export interface SaveSenderQrSessionInput {
  sessionId: string;
  displayName: string;
  fileCount: number;
  preparedBytes: number;
  frameDurationMs: number;
  frameTexts: readonly string[];
  status?: SenderQrSessionStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface SenderQrSessionPatch {
  status?: SenderQrSessionStatus;
  displayName?: string;
  frameDurationMs?: number;
  updatedAt?: string;
}

export interface LoadedSenderQrSession {
  record: SenderQrSessionRecord;
  frameTexts: string[];
}

export interface SaveReceiverQrSessionInput {
  sessionId: string;
  displayName?: string;
  status?: ReceiverQrSessionStatus;
  fileCount?: number;
  preparedBytes?: number;
  frameCount?: number;
  receivedFrames?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface StoredReceiverFrame {
  key: string;
  sessionId: string;
  kind: "metadata" | "data";
  index: number;
  text: string;
  updatedAt: number;
}

interface StoredSenderFrame {
  key: string;
  sessionId: string;
  orderIndex: number;
  text: string;
  updatedAt: number;
}

interface TransferProgressDatabase extends DBSchema {
  "qr-frames": {
    key: string;
    value: StoredReceiverFrame;
    indexes: {
      "by-session": string;
    };
  };
  "qr-sender-frames": {
    key: string;
    value: StoredSenderFrame;
    indexes: {
      "by-session": string;
    };
  };
  "qr-sessions": {
    key: string;
    value: QrSessionRecord;
    indexes: {
      "by-role": QrSessionRole;
      "by-updated-at": string;
    };
  };
}

interface DecodedFrameSetSummary {
  frameCount: number;
  preparedBytes: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isSafeInteger(value: unknown, minimum = 0): value is number {
  return Number.isSafeInteger(value) && typeof value === "number" && value >= minimum;
}

function isCanonicalTimestamp(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function isDisplayName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_DISPLAY_NAME_LENGTH &&
    value.trim() === value &&
    !Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f);
    })
  );
}

function isFrameDuration(value: unknown): value is number {
  return isSafeInteger(value, MIN_FRAME_DURATION_MS) && value <= MAX_FRAME_DURATION_MS;
}

function isSenderStatus(value: unknown): value is SenderQrSessionStatus {
  return value === "prepared" || value === "streaming" || value === "paused";
}

function isReceiverStatus(value: unknown): value is ReceiverQrSessionStatus {
  return (
    value === "receiving" ||
    value === "paused" ||
    value === "verified" ||
    value === "conflict"
  );
}

function hasValidBaseFields(value: Record<string, unknown>): boolean {
  return (
    value.schemaVersion === SESSION_SCHEMA_VERSION &&
    value.qrProtocolVersion === QR_PROTOCOL_VERSION &&
    typeof value.sessionId === "string" &&
    isValidSessionId(value.sessionId) &&
    isDisplayName(value.displayName) &&
    isCanonicalTimestamp(value.createdAt) &&
    isCanonicalTimestamp(value.updatedAt) &&
    value.updatedAt >= value.createdAt &&
    isSafeInteger(value.storedBytes)
  );
}

function isSenderSessionRecord(value: unknown): value is SenderQrSessionRecord {
  if (!isRecord(value) || !hasValidBaseFields(value)) {
    return false;
  }
  return (
    value.role === "sender" &&
    isSenderStatus(value.status) &&
    isSafeInteger(value.fileCount, 1) &&
    isSafeInteger(value.preparedBytes, 1) &&
    isSafeInteger(value.frameCount, 1) &&
    isFrameDuration(value.frameDurationMs)
  );
}

function isReceiverSessionRecord(value: unknown): value is ReceiverQrSessionRecord {
  if (!isRecord(value) || !hasValidBaseFields(value)) {
    return false;
  }
  if (
    value.role !== "receiver" ||
    !isReceiverStatus(value.status) ||
    !(value.fileCount === null || isSafeInteger(value.fileCount, 1)) ||
    !(value.preparedBytes === null || isSafeInteger(value.preparedBytes, 1)) ||
    !(value.frameCount === null || isSafeInteger(value.frameCount, 1)) ||
    !isSafeInteger(value.receivedFrames)
  ) {
    return false;
  }
  return value.frameCount === null || value.receivedFrames <= value.frameCount;
}

function isQrSessionRecord(value: unknown): value is QrSessionRecord {
  return isSenderSessionRecord(value) || isReceiverSessionRecord(value);
}

function isStoredReceiverFrame(value: unknown): value is StoredReceiverFrame {
  return (
    isRecord(value) &&
    typeof value.key === "string" &&
    typeof value.sessionId === "string" &&
    isValidSessionId(value.sessionId) &&
    (value.kind === "metadata" || value.kind === "data") &&
    isSafeInteger(value.index) &&
    typeof value.text === "string" &&
    value.text.length > 0 &&
    typeof value.updatedAt === "number" &&
    Number.isSafeInteger(value.updatedAt) &&
    value.updatedAt > 0
  );
}

function isStoredSenderFrame(value: unknown): value is StoredSenderFrame {
  return (
    isRecord(value) &&
    typeof value.key === "string" &&
    typeof value.sessionId === "string" &&
    isValidSessionId(value.sessionId) &&
    isSafeInteger(value.orderIndex) &&
    typeof value.text === "string" &&
    value.text.length > 0 &&
    typeof value.updatedAt === "number" &&
    Number.isSafeInteger(value.updatedAt) &&
    value.updatedAt > 0
  );
}

function assertSessionId(sessionId: string): void {
  if (!isValidSessionId(sessionId)) {
    throw new TypeError("A valid QR session ID is required.");
  }
}

function assertDisplayName(displayName: string): void {
  if (!isDisplayName(displayName)) {
    throw new TypeError("The QR session display name is invalid.");
  }
}

function assertPositiveInteger(value: number, label: string): void {
  if (!isSafeInteger(value, 1)) {
    throw new TypeError(`${label} must be a positive integer.`);
  }
}

function assertFrameDuration(frameDurationMs: number): void {
  if (!isFrameDuration(frameDurationMs)) {
    throw new TypeError(
      `Frame duration must be between ${MIN_FRAME_DURATION_MS.toString()} and ${MAX_FRAME_DURATION_MS.toString()} ms.`,
    );
  }
}

function timestampOrNow(value: string | undefined, label: string): string {
  const timestamp = value ?? new Date().toISOString();
  if (!isCanonicalTimestamp(timestamp)) {
    throw new TypeError(`${label} must be a canonical ISO timestamp.`);
  }
  return timestamp;
}

function assertTimestampOrder(createdAt: string, updatedAt: string): void {
  if (updatedAt < createdAt) {
    throw new TypeError("QR session update time cannot precede its creation time.");
  }
}

function sessionKey(role: QrSessionRole, sessionId: string): string {
  return `${role}|${sessionId}`;
}

function senderFrameKey(sessionId: string, orderIndex: number): string {
  return `${sessionId}|${orderIndex.toString().padStart(8, "0")}`;
}

function receiverFrameKey(
  sessionId: string,
  kind: "metadata" | "data",
  index: number,
): string {
  return `${sessionId}|${kind}|${index.toString()}`;
}

function encodedTextBytes(texts: readonly string[]): number {
  return texts.reduce((total, text) => total + textEncoder.encode(text).byteLength, 0);
}

async function validateSenderFrameTexts(
  sessionId: string,
  frameTexts: readonly string[],
): Promise<DecodedFrameSetSummary> {
  if (!Array.isArray(frameTexts) || frameTexts.length === 0) {
    throw new TypeError("A sender session needs at least one persisted QR frame.");
  }

  let expectedMetadataFrames: number | undefined;
  let expectedDataFrames: number | undefined;
  let expectedPreparedBytes: number | undefined;
  let expectedPackageDigest: string | undefined;
  const identities = new Set<string>();

  for (const text of frameTexts) {
    if (typeof text !== "string" || text.length === 0) {
      throw new TypeError("Sender QR frame text must be a non-empty string.");
    }

    let frame: TransferFrame;
    try {
      frame = await decodeTransferFrame(text);
    } catch {
      throw new TypeError("Sender QR frame text failed v1 validation.");
    }
    if (frame.sessionId !== sessionId) {
      throw new TypeError("Sender QR frames cannot mix session IDs.");
    }

    expectedMetadataFrames ??= frame.metadataFrameCount;
    expectedDataFrames ??= frame.dataFrameCount;
    expectedPreparedBytes ??= frame.packageByteLength;
    expectedPackageDigest ??= frame.packageSha256;
    if (
      frame.metadataFrameCount !== expectedMetadataFrames ||
      frame.dataFrameCount !== expectedDataFrames ||
      frame.packageByteLength !== expectedPreparedBytes ||
      frame.packageSha256 !== expectedPackageDigest
    ) {
      throw new TypeError("Sender QR frames contain conflicting transfer metadata.");
    }

    const identity = `${frame.kind}|${frame.index.toString()}`;
    if (identities.has(identity)) {
      throw new TypeError("Sender QR frames contain a duplicate frame index.");
    }
    identities.add(identity);
  }

  if (
    expectedMetadataFrames === undefined ||
    expectedDataFrames === undefined ||
    expectedPreparedBytes === undefined ||
    identities.size !== expectedMetadataFrames + expectedDataFrames
  ) {
    throw new TypeError("Sender QR frames do not contain one complete frame set.");
  }
  for (let index = 0; index < expectedMetadataFrames; index += 1) {
    if (!identities.has(`metadata|${index.toString()}`)) {
      throw new TypeError("Sender QR frames are missing a metadata frame.");
    }
  }
  for (let index = 0; index < expectedDataFrames; index += 1) {
    if (!identities.has(`data|${index.toString()}`)) {
      throw new TypeError("Sender QR frames are missing a data frame.");
    }
  }

  return {
    frameCount: identities.size,
    preparedBytes: expectedPreparedBytes,
  };
}

async function deriveReceiverFrameSummary(
  frames: readonly StoredReceiverFrame[],
): Promise<Pick<ReceiverQrSessionRecord, "frameCount" | "preparedBytes">> {
  for (const storedFrame of frames) {
    try {
      const frame = await decodeTransferFrame(storedFrame.text);
      const frameCount = frame.metadataFrameCount + frame.dataFrameCount;
      if (frames.length <= frameCount) {
        return {
          frameCount,
          preparedBytes: frame.packageByteLength,
        };
      }
    } catch {
      // Legacy rows predate session summaries. Keep them discoverable even if a
      // damaged frame cannot provide trustworthy totals.
    }
  }
  return {
    frameCount: null,
    preparedBytes: null,
  };
}

function openTransferDatabase(
  databaseName: string,
): Promise<IDBPDatabase<TransferProgressDatabase>> {
  return openDB<TransferProgressDatabase>(databaseName, DATABASE_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(RECEIVER_FRAME_STORE)) {
        const receiverFrames = database.createObjectStore(RECEIVER_FRAME_STORE, {
          keyPath: "key",
        });
        receiverFrames.createIndex("by-session", "sessionId");
      }
      if (!database.objectStoreNames.contains(SENDER_FRAME_STORE)) {
        const senderFrames = database.createObjectStore(SENDER_FRAME_STORE, {
          keyPath: "key",
        });
        senderFrames.createIndex("by-session", "sessionId");
      }
      if (!database.objectStoreNames.contains(SESSION_STORE)) {
        const sessions = database.createObjectStore(SESSION_STORE);
        sessions.createIndex("by-role", "role");
        sessions.createIndex("by-updated-at", "updatedAt");
      }
    },
    blocking(_currentVersion, _blockedVersion, event) {
      if (event.target instanceof IDBDatabase) {
        event.target.close();
      }
    },
  });
}

export class QrSessionStore {
  readonly #databasePromise: Promise<IDBPDatabase<TransferProgressDatabase>>;
  readonly #receiverSessionTails = new Map<string, Promise<void>>();

  public constructor(databaseName = "quotation-qr-transfer") {
    this.#databasePromise = openTransferDatabase(databaseName);
  }

  protected database(): Promise<IDBPDatabase<TransferProgressDatabase>> {
    return this.#databasePromise;
  }

  protected async serializeReceiverSession<T>(
    sessionId: string,
    operation: () => Promise<T>,
  ): Promise<T> {
    const previous = this.#receiverSessionTails.get(sessionId) ?? Promise.resolve();
    let release!: () => void;
    const gate = new Promise<void>((resolve) => {
      release = resolve;
    });
    const tail = previous.catch(() => undefined).then(async () => gate);
    this.#receiverSessionTails.set(sessionId, tail);

    await previous.catch(() => undefined);
    try {
      return await operation();
    } finally {
      release();
      if (this.#receiverSessionTails.get(sessionId) === tail) {
        this.#receiverSessionTails.delete(sessionId);
      }
    }
  }

  public async saveSenderSession(
    input: SaveSenderQrSessionInput,
  ): Promise<SenderQrSessionRecord> {
    assertSessionId(input.sessionId);
    assertDisplayName(input.displayName);
    assertPositiveInteger(input.fileCount, "Sender file count");
    assertPositiveInteger(input.preparedBytes, "Prepared byte length");
    assertFrameDuration(input.frameDurationMs);
    if (input.status !== undefined && !isSenderStatus(input.status)) {
      throw new TypeError("Sender QR session status is invalid.");
    }

    const summary = await validateSenderFrameTexts(input.sessionId, input.frameTexts);
    if (summary.preparedBytes !== input.preparedBytes) {
      throw new TypeError(
        "Persisted sender QR frames do not match the prepared byte length.",
      );
    }

    const database = await this.#databasePromise;
    const transaction = database.transaction(
      [SESSION_STORE, SENDER_FRAME_STORE],
      "readwrite",
    );
    const sessions = transaction.objectStore(SESSION_STORE);
    const senderFrames = transaction.objectStore(SENDER_FRAME_STORE);
    const key = sessionKey("sender", input.sessionId);
    const existingValue: unknown = await sessions.get(key);
    const existingRecord = isSenderSessionRecord(existingValue)
      ? existingValue
      : undefined;
    const existingFrameValues: unknown[] = await senderFrames
      .index("by-session")
      .getAll(input.sessionId);
    const existingFrames = existingFrameValues
      .filter(isStoredSenderFrame)
      .sort((left, right) => left.orderIndex - right.orderIndex);
    if (
      existingRecord !== undefined &&
      (existingFrames.length !== input.frameTexts.length ||
        existingFrames.some((frame, index) => frame.text !== input.frameTexts[index]))
    ) {
      transaction.abort();
      await transaction.done.catch(() => undefined);
      throw new TypeError(
        "This sender session ID is already bound to a different frame set.",
      );
    }

    const createdAt =
      existingRecord?.createdAt ??
      timestampOrNow(input.createdAt, "Sender creation time");
    const updatedAt = timestampOrNow(input.updatedAt, "Sender update time");
    assertTimestampOrder(createdAt, updatedAt);
    const record: SenderQrSessionRecord = {
      schemaVersion: SESSION_SCHEMA_VERSION,
      qrProtocolVersion: QR_PROTOCOL_VERSION,
      role: "sender",
      sessionId: input.sessionId,
      status: input.status ?? existingRecord?.status ?? "prepared",
      displayName: input.displayName,
      fileCount: input.fileCount,
      preparedBytes: input.preparedBytes,
      frameCount: summary.frameCount,
      frameDurationMs: input.frameDurationMs,
      createdAt,
      updatedAt,
      storedBytes: encodedTextBytes(input.frameTexts),
    };

    const oldKeys = await senderFrames.index("by-session").getAllKeys(input.sessionId);
    await Promise.all(oldKeys.map(async (oldKey) => senderFrames.delete(oldKey)));
    const updatedAtEpoch = Date.parse(updatedAt);
    await Promise.all(
      input.frameTexts.map(async (text, orderIndex) =>
        senderFrames.put({
          key: senderFrameKey(input.sessionId, orderIndex),
          sessionId: input.sessionId,
          orderIndex,
          text,
          updatedAt: updatedAtEpoch,
        }),
      ),
    );
    await sessions.put(record, key);
    await transaction.done;
    return structuredClone(record);
  }

  public async loadSenderSession(
    sessionId: string,
  ): Promise<LoadedSenderQrSession | undefined> {
    if (!isValidSessionId(sessionId)) {
      return undefined;
    }
    const database = await this.#databasePromise;
    const transaction = database.transaction(
      [SESSION_STORE, SENDER_FRAME_STORE],
      "readonly",
    );
    const recordValue: unknown = await transaction
      .objectStore(SESSION_STORE)
      .get(sessionKey("sender", sessionId));
    const frameValues: unknown[] = await transaction
      .objectStore(SENDER_FRAME_STORE)
      .index("by-session")
      .getAll(sessionId);
    await transaction.done;
    if (!isSenderSessionRecord(recordValue)) {
      return undefined;
    }

    const storedFrames = frameValues
      .filter(isStoredSenderFrame)
      .sort((left, right) => left.orderIndex - right.orderIndex);
    if (
      storedFrames.length !== frameValues.length ||
      storedFrames.length !== recordValue.frameCount ||
      storedFrames.some((frame, index) => frame.orderIndex !== index)
    ) {
      return undefined;
    }
    const frameTexts = storedFrames.map((frame) => frame.text);
    try {
      const summary = await validateSenderFrameTexts(sessionId, frameTexts);
      if (
        summary.frameCount !== recordValue.frameCount ||
        summary.preparedBytes !== recordValue.preparedBytes ||
        encodedTextBytes(frameTexts) !== recordValue.storedBytes
      ) {
        return undefined;
      }
    } catch {
      return undefined;
    }
    return {
      record: structuredClone(recordValue),
      frameTexts: [...frameTexts],
    };
  }

  public async updateSenderSession(
    sessionId: string,
    patch: SenderQrSessionPatch,
  ): Promise<SenderQrSessionRecord | undefined> {
    if (!isValidSessionId(sessionId)) {
      return undefined;
    }
    const patchValue: unknown = patch;
    if (!isRecord(patchValue)) {
      throw new TypeError("Sender QR session patch is invalid.");
    }
    const allowedKeys = new Set([
      "status",
      "displayName",
      "frameDurationMs",
      "updatedAt",
    ]);
    if (Object.keys(patchValue).some((key) => !allowedKeys.has(key))) {
      throw new TypeError("Sender QR session patch contains an unsupported field.");
    }
    if (patchValue.status !== undefined && !isSenderStatus(patchValue.status)) {
      throw new TypeError("Sender QR session status is invalid.");
    }
    if (patchValue.displayName !== undefined) {
      if (typeof patchValue.displayName !== "string") {
        throw new TypeError("The QR session display name is invalid.");
      }
      assertDisplayName(patchValue.displayName);
    }
    if (patchValue.frameDurationMs !== undefined) {
      if (typeof patchValue.frameDurationMs !== "number") {
        throw new TypeError("Sender QR frame duration is invalid.");
      }
      assertFrameDuration(patchValue.frameDurationMs);
    }
    if (
      patchValue.updatedAt !== undefined &&
      typeof patchValue.updatedAt !== "string"
    ) {
      throw new TypeError("Sender update time must be a canonical ISO timestamp.");
    }

    const database = await this.#databasePromise;
    const key = sessionKey("sender", sessionId);
    const transaction = database.transaction(SESSION_STORE, "readwrite");
    const value: unknown = await transaction.store.get(key);
    if (!isSenderSessionRecord(value)) {
      await transaction.done;
      return undefined;
    }
    const updatedAt = timestampOrNow(patchValue.updatedAt, "Sender update time");
    assertTimestampOrder(value.createdAt, updatedAt);
    const updated: SenderQrSessionRecord = {
      ...value,
      status: patchValue.status ?? value.status,
      displayName: patchValue.displayName ?? value.displayName,
      frameDurationMs: patchValue.frameDurationMs ?? value.frameDurationMs,
      updatedAt,
    };
    await transaction.store.put(updated, key);
    await transaction.done;
    return structuredClone(updated);
  }

  public async saveReceiverSession(
    input: SaveReceiverQrSessionInput,
  ): Promise<ReceiverQrSessionRecord> {
    assertSessionId(input.sessionId);
    if (input.displayName !== undefined) {
      assertDisplayName(input.displayName);
    }
    if (input.status !== undefined && !isReceiverStatus(input.status)) {
      throw new TypeError("Receiver QR session status is invalid.");
    }
    if (input.fileCount !== undefined) {
      assertPositiveInteger(input.fileCount, "Receiver file count");
    }
    if (input.preparedBytes !== undefined) {
      assertPositiveInteger(input.preparedBytes, "Receiver prepared byte length");
    }
    if (input.frameCount !== undefined) {
      assertPositiveInteger(input.frameCount, "Receiver frame count");
    }
    if (input.receivedFrames !== undefined && !isSafeInteger(input.receivedFrames)) {
      throw new TypeError("Receiver progress must be a non-negative integer.");
    }

    const serializedInput = { ...input };
    return this.serializeReceiverSession(input.sessionId, async () =>
      this.saveReceiverSessionSerialized(serializedInput),
    );
  }

  private async saveReceiverSessionSerialized(
    input: SaveReceiverQrSessionInput,
  ): Promise<ReceiverQrSessionRecord> {
    const database = await this.#databasePromise;
    const key = sessionKey("receiver", input.sessionId);
    const existingValue: unknown = await database.get(SESSION_STORE, key);
    const existingRecord = isReceiverSessionRecord(existingValue)
      ? existingValue
      : undefined;
    const frameValues: unknown[] = await database.getAllFromIndex(
      RECEIVER_FRAME_STORE,
      "by-session",
      input.sessionId,
    );
    const frames = frameValues.filter(isStoredReceiverFrame);
    const derived = await deriveReceiverFrameSummary(frames);
    if (
      derived.preparedBytes !== null &&
      input.preparedBytes !== undefined &&
      input.preparedBytes !== derived.preparedBytes
    ) {
      throw new TypeError(
        "Receiver session metadata conflicts with persisted frame bytes.",
      );
    }
    if (
      derived.frameCount !== null &&
      input.frameCount !== undefined &&
      input.frameCount !== derived.frameCount
    ) {
      throw new TypeError(
        "Receiver session metadata conflicts with persisted frame totals.",
      );
    }

    const createdAt =
      existingRecord?.createdAt ??
      timestampOrNow(input.createdAt, "Receiver creation time");
    const updatedAt = timestampOrNow(input.updatedAt, "Receiver update time");
    assertTimestampOrder(createdAt, updatedAt);
    const frameCount =
      input.frameCount ?? derived.frameCount ?? existingRecord?.frameCount ?? null;
    const receivedFrames = Math.max(
      frames.length,
      input.receivedFrames ?? existingRecord?.receivedFrames ?? 0,
    );
    if (frameCount !== null && receivedFrames > frameCount) {
      throw new TypeError("Receiver progress exceeds the expected frame count.");
    }
    const record: ReceiverQrSessionRecord = {
      schemaVersion: SESSION_SCHEMA_VERSION,
      qrProtocolVersion: QR_PROTOCOL_VERSION,
      role: "receiver",
      sessionId: input.sessionId,
      status: input.status ?? existingRecord?.status ?? "receiving",
      displayName:
        input.displayName ??
        existingRecord?.displayName ??
        DEFAULT_RECEIVER_DISPLAY_NAME,
      fileCount: input.fileCount ?? existingRecord?.fileCount ?? null,
      preparedBytes:
        input.preparedBytes ??
        derived.preparedBytes ??
        existingRecord?.preparedBytes ??
        null,
      frameCount,
      receivedFrames,
      createdAt,
      updatedAt,
      storedBytes: encodedTextBytes(frames.map((frame) => frame.text)),
    };
    await database.put(SESSION_STORE, record, key);
    return structuredClone(record);
  }

  public async listSessions(): Promise<QrSessionRecord[]> {
    await this.ensureReceiverSessionSummaries();
    const database = await this.#databasePromise;
    const values: unknown[] = await database.getAllFromIndex(
      SESSION_STORE,
      "by-updated-at",
    );
    return values
      .filter(isQrSessionRecord)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      .map((record) => structuredClone(record));
  }

  public async deleteSession(role: QrSessionRole, sessionId: string): Promise<void> {
    if (role !== "sender" && role !== "receiver") {
      throw new TypeError("QR session role is invalid.");
    }
    assertSessionId(sessionId);
    const database = await this.#databasePromise;

    if (role === "sender") {
      const transaction = database.transaction(
        [SESSION_STORE, SENDER_FRAME_STORE],
        "readwrite",
      );
      const frames = transaction.objectStore(SENDER_FRAME_STORE);
      const keys = await frames.index("by-session").getAllKeys(sessionId);
      await Promise.all(keys.map(async (key) => frames.delete(key)));
      await transaction
        .objectStore(SESSION_STORE)
        .delete(sessionKey("sender", sessionId));
      await transaction.done;
      return;
    }

    const transaction = database.transaction(
      [SESSION_STORE, RECEIVER_FRAME_STORE],
      "readwrite",
    );
    const frames = transaction.objectStore(RECEIVER_FRAME_STORE);
    const keys = await frames.index("by-session").getAllKeys(sessionId);
    await Promise.all(keys.map(async (key) => frames.delete(key)));
    await transaction
      .objectStore(SESSION_STORE)
      .delete(sessionKey("receiver", sessionId));
    await transaction.done;
  }

  public async close(): Promise<void> {
    const database = await this.#databasePromise;
    database.close();
  }

  private async ensureReceiverSessionSummaries(): Promise<void> {
    const database = await this.#databasePromise;
    const [frameValues, sessionValues] = await Promise.all([
      database.getAll(RECEIVER_FRAME_STORE) as Promise<unknown[]>,
      database.getAll(SESSION_STORE) as Promise<unknown[]>,
    ]);
    const existingReceiverKeys = new Set(
      sessionValues
        .filter(isReceiverSessionRecord)
        .map((record) => sessionKey("receiver", record.sessionId)),
    );
    const grouped = new Map<string, StoredReceiverFrame[]>();
    for (const frame of frameValues.filter(isStoredReceiverFrame)) {
      if (existingReceiverKeys.has(sessionKey("receiver", frame.sessionId))) {
        continue;
      }
      const frames = grouped.get(frame.sessionId) ?? [];
      frames.push(frame);
      grouped.set(frame.sessionId, frames);
    }
    if (grouped.size === 0) {
      return;
    }

    const backfilled: ReceiverQrSessionRecord[] = [];
    for (const [sessionId, frames] of grouped) {
      const sorted = [...frames].sort(
        (left, right) => left.updatedAt - right.updatedAt,
      );
      const first = sorted[0];
      const last = sorted.at(-1);
      if (first === undefined || last === undefined) {
        continue;
      }
      const derived = await deriveReceiverFrameSummary(sorted);
      backfilled.push({
        schemaVersion: SESSION_SCHEMA_VERSION,
        qrProtocolVersion: QR_PROTOCOL_VERSION,
        role: "receiver",
        sessionId,
        status: "receiving",
        displayName: DEFAULT_RECEIVER_DISPLAY_NAME,
        fileCount: null,
        preparedBytes: derived.preparedBytes,
        frameCount: derived.frameCount,
        receivedFrames: sorted.length,
        createdAt: new Date(first.updatedAt).toISOString(),
        updatedAt: new Date(last.updatedAt).toISOString(),
        storedBytes: encodedTextBytes(sorted.map((frame) => frame.text)),
      });
    }

    const transaction = database.transaction(SESSION_STORE, "readwrite");
    for (const record of backfilled) {
      const key = sessionKey("receiver", record.sessionId);
      const current: unknown = await transaction.store.get(key);
      if (!isReceiverSessionRecord(current)) {
        await transaction.store.put(record, key);
      }
    }
    await transaction.done;
  }
}

export class ReceiverProgressStore extends QrSessionStore {
  public async saveFrame(frame: TransferFrame): Promise<void> {
    assertSessionId(frame.sessionId);
    if (
      frame.version !== QR_PROTOCOL_VERSION ||
      (frame.kind !== "metadata" && frame.kind !== "data") ||
      !isSafeInteger(frame.index) ||
      typeof frame.text !== "string" ||
      frame.text.length === 0 ||
      !isSafeInteger(frame.metadataFrameCount, 1) ||
      !isSafeInteger(frame.dataFrameCount, 1) ||
      !isSafeInteger(frame.packageByteLength, 1)
    ) {
      throw new TypeError("Only a valid v1 QR transfer frame can be persisted.");
    }

    await this.serializeReceiverSession(frame.sessionId, async () =>
      this.saveFrameSerialized(frame),
    );
  }

  private async saveFrameSerialized(frame: TransferFrame): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction(
      [RECEIVER_FRAME_STORE, SESSION_STORE],
      "readwrite",
    );
    const receiverFrames = transaction.objectStore(RECEIVER_FRAME_STORE);
    const sessions = transaction.objectStore(SESSION_STORE);
    const updatedAtEpoch = Date.now();
    const storedFrame: StoredReceiverFrame = {
      key: receiverFrameKey(frame.sessionId, frame.kind, frame.index),
      sessionId: frame.sessionId,
      kind: frame.kind,
      index: frame.index,
      text: frame.text,
      updatedAt: updatedAtEpoch,
    };
    const existingFrameValue: unknown = await receiverFrames.get(storedFrame.key);
    if (
      existingFrameValue !== undefined &&
      (!isStoredReceiverFrame(existingFrameValue) ||
        existingFrameValue.text !== frame.text)
    ) {
      transaction.abort();
      await transaction.done.catch(() => undefined);
      throw new TypeError(
        "A different receiver frame already uses this session index.",
      );
    }
    const isNewFrame = existingFrameValue === undefined;
    await receiverFrames.put(storedFrame);

    const receivedFrames = await receiverFrames
      .index("by-session")
      .count(frame.sessionId);
    const expectedFrameCount = frame.metadataFrameCount + frame.dataFrameCount;
    if (receivedFrames > expectedFrameCount) {
      transaction.abort();
      await transaction.done.catch(() => undefined);
      throw new TypeError("Persisted receiver progress contains invalid frame rows.");
    }

    const recordKey = sessionKey("receiver", frame.sessionId);
    const existingValue: unknown = await sessions.get(recordKey);
    const existingRecord = isReceiverSessionRecord(existingValue)
      ? existingValue
      : undefined;
    if (
      (existingRecord?.preparedBytes !== null &&
        existingRecord?.preparedBytes !== undefined &&
        existingRecord.preparedBytes !== frame.packageByteLength) ||
      (existingRecord?.frameCount !== null &&
        existingRecord?.frameCount !== undefined &&
        existingRecord.frameCount !== expectedFrameCount)
    ) {
      transaction.abort();
      await transaction.done.catch(() => undefined);
      throw new TypeError("Receiver frame metadata conflicts with this local session.");
    }

    const updatedAt = new Date(updatedAtEpoch).toISOString();
    let storedBytes =
      (existingRecord?.storedBytes ?? 0) +
      (isNewFrame ? textEncoder.encode(frame.text).byteLength : 0);
    if (existingRecord === undefined) {
      const frameValues: unknown[] = await receiverFrames
        .index("by-session")
        .getAll(frame.sessionId);
      const frames = frameValues.filter(isStoredReceiverFrame);
      if (frames.length !== frameValues.length) {
        transaction.abort();
        await transaction.done.catch(() => undefined);
        throw new TypeError("Persisted receiver progress contains invalid frame rows.");
      }
      storedBytes = encodedTextBytes(frames.map((value) => value.text));
    }
    const record: ReceiverQrSessionRecord = {
      schemaVersion: SESSION_SCHEMA_VERSION,
      qrProtocolVersion: QR_PROTOCOL_VERSION,
      role: "receiver",
      sessionId: frame.sessionId,
      status: existingRecord?.status ?? "receiving",
      displayName: existingRecord?.displayName ?? DEFAULT_RECEIVER_DISPLAY_NAME,
      fileCount: existingRecord?.fileCount ?? null,
      preparedBytes: frame.packageByteLength,
      frameCount: expectedFrameCount,
      receivedFrames,
      createdAt: existingRecord?.createdAt ?? updatedAt,
      updatedAt,
      storedBytes,
    };
    await sessions.put(record, recordKey);
    await transaction.done;
  }

  public async loadFrameTexts(sessionId: string): Promise<string[]> {
    if (!isValidSessionId(sessionId)) {
      return [];
    }
    const database = await this.database();
    const records = await database.getAllFromIndex(
      RECEIVER_FRAME_STORE,
      "by-session",
      sessionId,
    );
    return records
      .filter(isStoredReceiverFrame)
      .sort((left, right) => {
        if (left.kind !== right.kind) {
          return left.kind === "metadata" ? -1 : 1;
        }
        return left.index - right.index;
      })
      .map((record) => record.text);
  }

  public async clearSession(sessionId: string): Promise<void> {
    await this.deleteSession("receiver", sessionId);
  }
}
